import React from "react";
import { api } from "../../api/api";
import LinearProgress from "@mui/material/LinearProgress";
import { ISubscriptionPlan } from "../../redux/subscription/subscription.types";

export enum userRoles {
  ADMIN = "ADMIN",
  USER = "USER",
  None = "None",
  SUPERADMIN = "SUPERADMIN",
  ENR_ADMIN = "ENR_ADMIN",
  OWNER = "OWNER",
  WHATSAPPADMIN = "WHATSAPPADMIN",
  WHATSAPPUSER = "WHATSAPPUSER",
  MANAGER = "MANAGER",
  FINANCE = "FINANCE",
}
export interface IAuthUserCompanyOutlet {
  company_outlet_uuid: string;
  company_outlet_name: string;
}

export interface IAuth {
  user: {
    name: string;
    userId: number;
    email: string;
    isLogin: boolean;
    role: keyof typeof userRoles;
    tokens: { access: string } | null;
    userUuid: string;
    sessionCount: number | null;
    role_value: string;
    affilated_company_outlet_uuids: IAuthUserCompanyOutlet[];
    subscribed_plan: ISubscriptionPlan | null
  };
  twoFactorData: {
    userFactId: string;
  };
  login: (
    emailOrUsername: string,
    password: string,
    onSuccess: (isOTPPreview: boolean, user: IAuth["user"]) => void,
    onError: (message: string) => void
  ) => void;
  signup: (
    data: any,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => void;
  resendOTP: () => void;
  verifyOTP: (
    otp: string,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => void;
  forgotPassword: (
    email: string,
    otp_for: string,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => void;
  setNewPassword: (
    email: string,
    otp: string,
    new_password: string,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => void;
  logout: () => void;
  logoutFromAllDevices: () => void;
  addUserName: (name: string) => void;
  otherUserLogin: (
    token: string,
    userId: string,
    affilated_company_outlet_uuids: IAuthUserCompanyOutlet[],
    onComplete: (isSuccess: boolean, error?: string) => void
  ) => void;
  updateUserData: (userData: Partial<IAuth["user"]>) => void;
  saveSignupData: (data: any, onSuccess: () => void) => void;
}

const AuthContext = React.createContext<IAuth>({
  user: {
    name: "",
    email: "",
    userId: 0,
    role: userRoles.None,
    isLogin: false,
    tokens: null,
    userUuid: "",
    sessionCount: null,
    role_value: "",
    affilated_company_outlet_uuids: [],
    subscribed_plan: null
  },
  twoFactorData: {
    userFactId: "",
  },
  login: () => {},
  signup: () => {},
  resendOTP: () => {},
  verifyOTP: () => {},
  forgotPassword: () => {},
  setNewPassword: () => {},
  logout: () => {},
  logoutFromAllDevices: () => {},
  addUserName: () => {},
  otherUserLogin: () => {},
  updateUserData: () => {},
  saveSignupData: () => {},
});

export const useAuth = () => React.useContext(AuthContext);

// Global reference to updateUserData function
let globalUpdateUserData: ((userData: Partial<IAuth["user"]>) => void) | null =
  null;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = (
  props
) => {
  const [user, setUser] = React.useState<IAuth["user"]>({
    name: "",
    email: "",
    userId: 0,
    role: userRoles.None,
    isLogin: false,
    tokens: null,
    userUuid: "",
    sessionCount: null,
    role_value: "",
    affilated_company_outlet_uuids: [],
    subscribed_plan: null
  });
  const [twoFactorData, setTwoFactorData] = React.useState<
    IAuth["twoFactorData"]
  >({
    userFactId: "",
  });
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const auth = localStorage.getItem("auth");
    if (auth) setUser(JSON.parse(auth));
    // if (auth) {
    //   const data = JSON.parse(auth);
    //   const date1 = new Date(data.date);
    //   const date2 = new Date();
    //   const diff = date2.getHours() - date1.getHours();
    //   if (diff <= 1) {
    //     setUser(data);
    //   }
    // }
    setLoading(false);
  }, []);

  const handleLogin = async (
    emailOrUsername: string,
    password: string,
    onSuccess: (isOTPPreview: boolean, user: IAuth["user"]) => void,
    onError: (message: string) => void
  ) => {
    try {
      const res = await api.post("/authentication/login", {
        emailOrUsername: emailOrUsername,
        password: password,
      });
      const data = res.data;

      if (res.data.data.force_logout === true) {
        onError(res.data.message);
        localStorage.setItem(
          "auth",
          JSON.stringify({
            userUuid: res.data.data.user_uuid,
            force_logout: "true",
          })
        );
      }

      const name = res.data.data.user.full_name;

      const userData = {
        name: name,
        email: data.data.user.email,
        role: data.data.user.role_name,
        isLogin: data.data.isOTPPreview === false ? true : false,
        userId: data.data.user.user_fact_id,
        tokens: data.data.token,
        userUuid: data.data.user.user_uuid,
        sessionCount: data.data.sessionCount,
        role_value: data.data.user.role_value,
        force_logout: "false",
        affilated_company_outlet_uuids:  data.data.user.affilated_company_outlet_uuids,
        subscribed_plan: data.data.user.subscribed_plan,
      };

      setUser(userData);
      setPassword(password);
      onSuccess(!userData.isLogin, userData);

      if (userData.isLogin) {
        localStorage.setItem(
          "auth",
          JSON.stringify({ ...userData, date: new Date() })
        );
        const storedData = localStorage.getItem("auth");
      } else {
        setTwoFactorData({ userFactId: data.data.userFactId });
      }
    } catch (error: any) {
      onError(error.response?.data?.message || "An unexpected error occurred");
    }
  };

  interface SignupData {
    email: string;
    username: string;
    user_password: string;
    first_name: string;
    last_name: string;
    company_outlet_name: string;
    status: string;
  }

  const handleSignup = async (
    data: SignupData,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    try {
      const res = await api.post("/user/signup-user", data);

      const userData = {
        name: res.data.data.user.full_name,
        email: res.data.data.user.email,
        role: res.data.data.user.role_name,
        isLogin: true,
        userId: res.data.data.user.user_fact_id,
        tokens: res.data.data.token,
        userUuid: res.data.data.user.user_uuid,
        sessionCount: res.data.data.sessionCount,
        role_value: res.data.data.user.role_value,
        force_logout: res.data.data.force_logout ? "true" : "false",
        affilated_company_outlet_uuids: res.data.data.user.affilated_company_outlet_uuids,
        subscribed_plan: res.data.data.user.subscribed_plan,
      };

      if (res.data.data.token) {
        const user = {
          ...userData,
          isLogin: true,
        };

        // Explicitly set the user in state
        setUser(user);

        // Store in localStorage with a timestamp
        const authData = JSON.stringify({
          ...user,
          date: new Date().toISOString(),
        });
        localStorage.setItem("auth", authData);

        onSuccess();
      }
    } catch (error: any) {
      onError(error.response?.data?.message || "An unexpected error occurred");
    }
  };
  const saveSignupData = (data: any, onSuccess: () => void) => {
    const { user } = data;
    const userData = {
      name: user.full_name,
      email: user.email,
      role: user.role_name,
      isLogin: true,
      userId: user.user_fact_id,
      tokens: data.token,
      userUuid: user.user_uuid,
      sessionCount: data.sessionCount,
      role_value: user.role_value,
      force_logout: user.force_logout ? "true" : "false",
      affilated_company_outlet_uuids: data.user.affilated_company_outlet_uuids,
      subscribed_plan: data.user.subscribed_plan,
    };
    const authData = JSON.stringify({
      ...userData,
      date: new Date().toISOString(),
    });
    localStorage.setItem("auth", authData);
    setUser(userData);
    onSuccess();
  };
  const resendOTP = async () => {
    try {
      await api.post("/authentication/login", {
        email: user.email,
        password: password,
      });
    } catch (error: any) {
      console.log(error);
    }
  };

  const handleVerfiyOTP = async (
    otp: string,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    try {
      const res = await api.post("/user/validate-otp-get-token", {
        user_fact_id: twoFactorData.userFactId,
        otp: Number(otp),
      });
      const data = res.data;

      const userData = {
        ...user,
        isLogin: true,
        tokens: { access: data.data.access_token },
      };
      setUser(userData);
      localStorage.setItem(
        "auth",
        JSON.stringify({ ...userData, date: new Date() })
      );
      onSuccess();
    } catch (error: any) {
      onError(error.response.data.message);
    }
  };

  const handleLogout = async () => {
    const auth = localStorage.getItem("auth");
    if (auth) {
      const data = JSON.parse(auth);
      try {
        await api.put("/authentication/logout", {
          user_uuid: data.userUuid,
          sessionCount: data.sessionCount,
        });
      } catch (error: any) {
        console.error("Logout API error:", error.response.data.message);
      }
    }
    localStorage.clear();
    window.open("/auth/login", "_self");
  };

  const handleLogoutFromAllDevices = async () => {
    const auth = localStorage.getItem("auth");
    if (auth) {
      const data = JSON.parse(auth);

      try {
        await api.put("/authentication/logout", {
          user_uuid: data.userUuid,
          sessionCount: 0,
        });
      } catch (error: any) {
        console.error("Logout API error:", error.response.data.message);
      }
    }
    localStorage.clear();
    window.open("/auth/login", "_self");
  };

  const handleForgotPassword = async (
    email: string,
    otp_for: string,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    try {
      // New API: Single endpoint for both email and mobile
      const payload = {
        emailOrMobile: email,
        action: "SEND",
      };

      await api.post("/authentication/forget-password", payload);
      onSuccess();
    } catch (error: any) {
      onError(error.response.data.message);
    }
  };

  const handleNewPassword = async (
    email: string,
    otp: string,
    new_password: string,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    try {
      // New API: Single endpoint with emailOrMobile, otp, and password
      const payload = {
        emailOrMobile: email,
        otp: Number(otp),
        user_password: new_password,
      };

      await api.post("/authentication/forget-password", payload);
      onSuccess();
    } catch (error: any) {
      onError(error.response.data.message);
    }
  };

  const addUserName = (name: string) => {
    setUser({ ...user, name: name });
  };

  const updateUserData = React.useCallback(
    (userData: Partial<IAuth["user"]>) => {
      setUser((prevUser) => ({ ...prevUser, ...userData }));
    },
    []
  );

  // Set global reference to updateUserData
  React.useEffect(() => {
    globalUpdateUserData = updateUserData;
  }, [updateUserData]);

  const otherUserAccess = async (
    token: any,
    userId: any,
    affilated_company_outlet_uuids: IAuthUserCompanyOutlet[],
    onComplete: (isSuccess: boolean, error?: string) => void
  ) => {
    try {
      setLoading(true);

      const userData = {
        ...user,
        isLogin: true,
        userId: userId,
        userUuid: userId,
        tokens: token,
        affilated_company_outlet_uuids: affilated_company_outlet_uuids || [],
      };
      setUser(userData);
      localStorage.setItem(
        "auth",
        JSON.stringify({ ...userData, date: new Date() })
      );
      onComplete(true);
      setLoading(false);
    } catch (error: any) {
      // onError(error.response.data.message);
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <AuthContext.Provider
      value={{
        user: user,
        login: handleLogin,
        signup: handleSignup,
        twoFactorData,
        verifyOTP: handleVerfiyOTP,
        forgotPassword: handleForgotPassword,
        setNewPassword: handleNewPassword,
        logout: handleLogout,
        logoutFromAllDevices: handleLogoutFromAllDevices,
        resendOTP: resendOTP,
        addUserName: addUserName,
        otherUserLogin: otherUserAccess,
        updateUserData: updateUserData,
        saveSignupData: saveSignupData,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export const handleCompanySwitchAsync = async (
  company_outlet_uuid: string,
  onSuccess: (isSuccess: boolean) => void,
  onError: (message: string) => void
) => {
  try {
    const response = await api.post("/authentication/switch-company", {
      company_outlet_uuid: company_outlet_uuid,
    });

    const { data } = response.data;

    // Update the token in localStorage and context
    if (data.token) {
      // Get current auth data from localStorage
      const currentAuth = localStorage.getItem("auth");
      if (currentAuth) {
        const authData = JSON.parse(currentAuth);

        // Update with new token and user data
        const updatedAuthData = {
          ...authData,
          tokens: data.token,
          user: {
            ...authData.user,
            ...data.user,
            affilated_company_outlet_uuids:
              data.user.affilated_company_outlet_uuids,
          },
          sessionCount: data.sessionCount,
          date: new Date(),
        };

        // Save updated auth data to localStorage
        localStorage.setItem("auth", JSON.stringify(updatedAuthData));

        // Update the API default headers with new token
        api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;

        // Update the user context state
        if (globalUpdateUserData) {
          globalUpdateUserData({
            tokens: data.token,
            affilated_company_outlet_uuids:
              data.user.affilated_company_outlet_uuids,
            sessionCount: data.sessionCount,
          });
        }
      }
    }

    onSuccess(true);
  } catch (error: any) {
    onSuccess(false);
    onError(error.response?.data?.message || "Failed to switch company");
  }
};
